import { IS_CHANGE_EMAIL_ENABLED } from '@/app-shell/flags/auth-flags';
import { useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { useAuthSessionMetadata } from '@heidi/auth/state';
import { useUser } from '@heidi/workspace/data';

/** The one Auth0 connection strategy whose email we own and can change. */
const AUTH0_DB_STRATEGY = 'auth0';

/**
 * `AuthSessionMetadata.user` is deliberately `unknown` — each provider puts its
 * own shape there (Auth0User, KindeUser, a NextAuth Session). Only Auth0 has a
 * `sub`, so narrowing to it is also what keeps Kinde / Keycloak / FHIR sessions
 * out of the check below.
 */
const readSub = (user: unknown): string | null => {
  if (!user || typeof user !== 'object') return null;
  const { sub } = user as { sub?: unknown };
  return typeof sub === 'string' ? sub : null;
};

/**
 * Whether the signed-in account's email is owned by an upstream identity
 * provider, which makes a self-service change impossible.
 *
 * Auth0 mints `sub` as `<connection-strategy>|<subject>`, and only its database
 * connection uses the `auth0` strategy. Every other strategy —
 * `google-oauth2`, `apple`, `windowslive`, `samlp`, `waad`, `oidc` — is
 * federated: the upstream provider owns the email and Auth0 rejects the update,
 * so ml-scribe answers 403 at step one of the flow (MUL-2074, ml-scribe #18272).
 *
 * Fails open on anything it cannot read, so a boot-transient session or a
 * non-Auth0 provider keeps the affordance exactly as it is today: no `sub`, a
 * `sub` with no strategy segment, or an empty segment all read as "not known to
 * be federated". If one slips through, the backend still refuses it before
 * sending any verification code.
 *
 * DELIBERATELY NOT the user record's `is_social_login`, even though `mfa.tsx`
 * in this directory reads it. That claim is SESSION-level: Auth0's `set token
 * claims` action sets it from `event.connection.strategy`, the connection used
 * for THIS login ("Session-level: keyed off the current connection strategy").
 * Since the account-linking action calls `setPrimaryUser`, a user whose Auth0
 * PRIMARY identity is the database one can sign in through a linked Google
 * identity and read `is_social_login: true` — while their email is in fact
 * changeable, because Auth0's PATCH restriction keys on the primary identity,
 * which is exactly what `sub` carries. Hiding the button there would remove a
 * working capability, and ml-scribe's own pre-check (which reads the broker's
 * `auth0_id`, also the primary) would not agree with us. `mfa.tsx` is right to
 * read it: MFA availability IS a property of the session. Email ownership is a
 * property of the account. The claim also stops at Google/Apple, leaving
 * enterprise strategies to `is_sso_login` — which MUL-2074 found absent on some
 * SSO sessions, and which this check covers as a side effect.
 *
 * Allow-listing `auth0` rather than deny-listing known federated strategies is
 * the deliberate direction to fail in: a strategy added to the tenant later is
 * refused rather than silently treated as changeable. The Scribe tenant
 * declares three connection strategies today — `auth0`, `google-oauth2`,
 * `apple` — so there is no passwordless (`email`, `sms`) connection to misread.
 */
const hasFederatedLogin = (user: unknown): boolean => {
  const sub = readSub(user);
  if (!sub) return false;
  const separator = sub.indexOf('|');
  if (separator === -1) return false;
  const strategy = sub.slice(0, separator).toLowerCase();
  if (!strategy) return false;
  return strategy !== AUTH0_DB_STRATEGY;
};

/**
 * Whether the "Change email" affordance should render.
 *
 * Shared so callers that only link *to* the email field can match the field's
 * own gate — a CTA that routes there while this is false drops the user on a
 * disabled input with no way forward.
 */
export const useCanChangeEmail = (): boolean => {
  // Subscription only — the value is read imperatively below. Without it
  // nothing re-renders when PostHog resolves the flag mid-mount.
  void useFeatureFlagEnabled('release-change-email');

  const { data: isSsoLogin } = useUser((data) => data.is_sso_login === true);

  // Enterprise SSO was already blocked off the user record; social logins were
  // the gap, and they are only visible in the token's `sub`.
  const isFederatedLogin = hasFederatedLogin(useAuthSessionMetadata()?.user);

  return (IS_CHANGE_EMAIL_ENABLED() ?? false) && !isSsoLogin && !isFederatedLogin;
};
